var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-conversation-list> — ONE list of conversations, used everywhere there is one.
 *
 * IT IS THE COLUMNS, AND NOTHING ELSE. The scrolling, virtualization, sorting, resizing and
 * selection all live in `<vitesse-record-grid>`, which this renders; what belongs here is the
 * description of what a conversation row shows and how each column sorts. That split is what let
 * the agent-audit surface have the identical grid without a second implementation of any of it.
 *
 * There were two: the chat's recents panel and the AI console's transcript browser. Same rows, same
 * job, written twice — and they had drifted the way two implementations of one thing always do, to
 * the point that the same untitled thread was unrecognisable depending on which screen you were on.
 *
 * It has the symbol tree's shape, because that is the product's shape for a list of records: a
 * bordered frame, a grey toolbar holding the search, a header, and rows on a shared column template
 * with dividers between them. The selector is its own COLUMN rather than something tucked inside the
 * row, so every checkbox down the list sits on one axis.
 *
 * VIRTUALIZED, with no "load more". Scrolling asks for the next page as the reader approaches the
 * end and the rows arrive underneath them; the DOM holds a window regardless of how many
 * conversations exist, so a thousand rows cost what twenty do. A button that must be clicked to see
 * more is the reader doing the paging by hand.
 *
 * IT AUTHORS NO CSS AND CARRIES NO COPY OF ANY. It renders the SAME classes the symbol tree renders
 * — `.vg-wrapper`, `.vg-toolbar`, `.vg-header`, `.vg-row`, `.vg-cell` — which live in
 * `styles/components/base-grid-chrome.scss`. A previous version hand-copied those rules into a Lit
 * stylesheet so it could work inside the panel's shadow root, and within a day the copy had drifted
 * from the original on padding, font size, font weight and border tint: the same list, visibly
 * different on two pages. One definition, in light DOM, is the only arrangement with nothing to
 * keep in step.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { statusBadge } from '../components/templates';
import { LocaleController } from '../i18n/LocaleController';
import { listRow } from '../components/templates';
import { fmtCost, fmtDate, fmtDateTime, fmtNum, fmtTime } from '../format';
import '../grid/record-grid';
import '../elements/vitesse-icon';
/**
 * WHAT A THREAD IS CALLED — one answer, for every surface.
 *
 * The word "Untitled" is gone, and not because it was ugly. It is a label announcing that the
 * software failed to do something, shown to the person instead of to us; it is identical on every
 * row, so a list of five unnamed threads could not be read at all; and it was untranslated in three
 * of the five locales, so most of the world saw an English placeholder either way.
 *
 * A thread always has a first thing said, and that is a better name than any generated one would be
 * in the seconds before the generated one arrives. The title still wins when there is one. Only a
 * genuinely empty thread — created, nothing said yet — falls through, and there "New chat" is a
 * true statement rather than an apology.
 *
 * Exported and shared because the same thread must not read differently depending on which screen
 * you are on. That was the original argument for the shared list element; this is the same argument
 * one level in, for the string inside it.
 */
export const conversationLabel = (r) => r.title?.trim() || r.preview?.trim() || t('ai.newConversation');
/**
 * The columns of a conversation row.
 *
 * A function rather than a constant because the labels are translated, and a constant would bake in
 * whichever language happened to be active when the module was first evaluated — a locale switch
 * would then leave the headers in the old one.
 */
/*
 * HOW A SPAWNED AGENT'S ROW READS — indent plus badge, defined once.
 *
 * The console's grid had it and the rail did not, so the same agent was a nested row on one surface
 * and an unexplained top-level thread on the other — which is the surface people actually use. Both
 * halves are needed: the indent says whose it is, the badge says what it is, and either alone reads
 * as an accident on a virtualised list.
 *
 * `depth` is a number rather than a boolean because agents spawn agents, and the day one does the
 * list should indent it twice rather than flatten the second level into the first.
 */
export const AGENT_INDENT_PX = 16;
export const agentIndent = (depth) => (depth ? `padding-left:${depth * AGENT_INDENT_PX}px` : '');
export const agentBadge = (depth) => depth ? html `<span class="cl-agent">${statusBadge('blue', t('ai.subagent'))}</span>` : nothing;
export function conversationColumns() {
    return [
        {
            key: 'title',
            label: t('ai.colTitle'),
            // No width: the title flexes and takes whatever the fixed columns leave it.
            cell: (r) => {
                const title = conversationLabel(r);
                /*
                 * AN AGENT'S THREAD SITS UNDER THE TURN THAT STARTED IT.
                 *
                 * Indent says whose it is, and the badge says what it is — a spawned agent rather than
                 * something the reader opened. Both are needed: indent alone reads as a styling accident on
                 * a virtualised list, and a badge alone leaves the row floating beside its parent.
                 *
                 * `depth` is a number rather than a boolean because agents spawn agents, and the day one
                 * does the list should indent it twice rather than flatten the second level into the first.
                 */
                const depth = r.depth ?? 0;
                return html `<span class="cl-ellipsis" title=${title} style=${agentIndent(depth)}>
          ${agentBadge(depth)}${title}
        </span>`;
            },
            sort: (a, b) => (a.title || '').localeCompare(b.title || ''),
            desc: false, // text reads naturally A→Z
        },
        {
            key: 'messages',
            label: t('ai.colMessages'),
            width: 110,
            cell: (r) => r.messageCount,
            sort: (a, b) => a.messageCount - b.messageCount,
        },
        // Absent is not zero. A thread older than token attribution has nothing recorded, and a
        // confident 0 would read as "this conversation used no tokens" — so it sorts as -1, which
        // groups "not recorded" apart from a real zero rather than mixing into it.
        {
            key: 'tin',
            label: t('ai.colTokensIn'),
            width: 120,
            cell: (r) => fmtNum(r.inputTokens),
            sort: (a, b) => (a.inputTokens ?? -1) - (b.inputTokens ?? -1),
        },
        {
            key: 'tout',
            label: t('ai.colTokensOut'),
            width: 130,
            cell: (r) => fmtNum(r.outputTokens),
            sort: (a, b) => (a.outputTokens ?? -1) - (b.outputTokens ?? -1),
        },
        { key: 'at', label: t('ai.colDate'), width: 120, cell: (r) => fmtDate(r.atMs), sort: (a, b) => a.atMs - b.atMs },
        { key: 'at', label: t('ai.colTime'), width: 110, cell: (r) => fmtTime(r.atMs), sort: (a, b) => a.atMs - b.atMs },
    ];
}
let VitesseConversationList = class VitesseConversationList extends LitElement {
    constructor() {
        super(...arguments);
        this.rows = null;
        this.total = -1;
        this.pageScroll = false;
        this.filtered = false;
        this.emptyLabel = '';
        /**
         * COMPACT, for a narrow rail rather than a page.
         *
         * The chat panel is about 360px wide. Six columns in 360px is not a table, it is six unreadable
         * stubs — it rendered as "Title | Mes | To | Tol | Date | Time", which is the shape of a grid and
         * the information content of nothing. So the rail gets the two-line row every other roster in the
         * product uses, and the console gets the grid.
         *
         * WHAT STAYS SHARED IS THE DESCRIPTION, which was always the point: `conversationSubtitle` and
         * the row shape are one definition, so the same thread reads the same way in both places. Only
         * the layout differs, and it differs because 360px and 990px are genuinely different problems.
         */
        this.compact = false;
        /** The exact instant rather than "2 days ago" — an audit view needs something that can be
         *  cross-referenced against a log. Only meaningful in `compact`, where a sub-line renders. */
        this.precise = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        /**
         * The rail: one row per thread, title over a grey line, and nothing else.
         *
         * `listRow` is the row the MCP-server and model-provider rosters use, so a conversation in the
         * rail sits at the same rhythm as every other list in the product rather than at a rhythm of its
         * own. `onReveal` fires on hover rather than on scroll position: this list has no virtualizer, and
         * a row the pointer has reached is a row the reader is about to want the next page after.
         */
        /**
         * WHICH PARENTS ARE FOLDED SHUT.
         *
         * Held here rather than by the host because it is this view's business — which disclosures are
         * open is a property of looking at a list, not of the list's contents, and a host that owned it
         * would have to carry it through every re-render for no benefit. The Set is replaced rather than
         * mutated so Lit's identity comparison actually re-renders.
         *
         * Collapsed-by-DEFAULT is deliberately not the choice: a thread that fanned out to four agents
         * should say so on arrival. The chevron is there to quieten a busy list, not to hide it.
         */
        this.shut = new Set();
    }
    /** LIGHT DOM, so the global grid chrome reaches the element this renders. */
    createRenderRoot() {
        return this;
    }
    render() {
        if (this.compact)
            return this.renderCompact();
        return html `<vitesse-record-grid
      .cols=${conversationColumns()}
      .rows=${this.rows}
      .rowLabel=${(r) => conversationLabel(r)}
      .onPick=${this.onPick}
      .onReveal=${this.onReveal}
      .selected=${this.selected}
      .onSelect=${this.onSelect}
      .toolbar=${this.toolbar}
      .actions=${this.actions}
      .total=${this.total}
      ?pageScroll=${this.pageScroll}
      ?filtered=${this.filtered}
      .emptyLabel=${this.emptyLabel}
    ></vitesse-record-grid>`;
    }
    toggle(id) {
        const next = new Set(this.shut);
        if (!next.delete(id))
            next.add(id);
        this.shut = next;
    }
    /**
     * The rows, grouped as PARENT + the agents its turns started.
     *
     * The data stays flat — a row is a row, and depth is a property of it, which is what keeps
     * windowing and keyboard order working — and this is a reading of that flatness, not a change to
     * it. Consecutive deeper rows belong to the last depth-0 row above them, which is exactly how the
     * hub builds the list and the only relationship the flattening preserves.
     */
    grouped(rows) {
        const out = [];
        rows.forEach((row, index) => {
            if ((row.depth ?? 0) === 0 || !out.length)
                out.push({ row, index, agents: [] });
            else
                out[out.length - 1].agents.push({ row, index });
        });
        return out;
    }
    renderCompact() {
        // base-empty (not a bespoke ai-empty) so "no conversations yet" reads as the SAME centred,
        // muted empty state every other list in the product shows — see _panel.scss. The rail's CSS
        // gives it the full height so it centres in the pane rather than clinging to the top.
        if (this.rows === null)
            return html `<div class="base-empty">${t('ai.loadingConversations')}</div>`;
        if (this.rows.length === 0)
            return html `<div class="base-empty">${this.emptyLabel || t('ai.noConversations')}</div>`;
        // --fill: this rail is not the six-row panel cap — it grows with its rows until it reaches the
        // links below, then scrolls inside itself. The rail scoping lives in _ai-hub.scss.
        // The SAME reading the console's grid gives it. This rail rendered an agent as an ordinary
        // top-level thread — so the one surface people use showed the nesting the other one had.
        const one = (r, i, control) => html `
      <div @mouseenter=${() => this.onReveal?.(i)}>
        ${listRow({
            title: conversationLabel(r),
            mono: false,
            // `control`, not `leading`: the latter goes inside the row's content column, which stacks
            // it above the title and puts it inside the button that opens the thread.
            control,
            badge: agentBadge(r.depth ?? 0),
            // On the ROW, not on a wrapper: the divider is the row's own border, stretched past the
            // card's edges, so indenting the wrapper indented the line too.
            indentPx: (r.depth ?? 0) * AGENT_INDENT_PX,
            sub: conversationSubtitle(r, this.precise, false),
            onOpen: () => this.onPick?.(r.id, r),
        })}
      </div>
    `;
        return html `<div class="base-roster-list base-roster-list--fill ai-conv-rail">
      ${this.grouped(this.rows).map((g) => {
            const open = !this.shut.has(g.row.id);
            /*
             * THE CHEVRON, and only where there is something behind it.
             *
             * A disclosure control on a thread that started nothing is a control that does nothing, and
             * a column of them would suggest every row hides something. Rows without agents render
             * exactly as they did.
             *
             * `stopPropagation` because it sits inside a row whose whole surface opens the thread —
             * without it, folding a group would also navigate away from the list you are folding.
             */
            const chevron = g.agents.length
                ? html `<button
              class="cl-chevron ${open ? 'open' : ''}"
              aria-expanded=${open ? 'true' : 'false'}
              title=${open ? t('ai.collapseAgents') : t('ai.expandAgents')}
              aria-label=${open ? t('ai.collapseAgents') : t('ai.expandAgents')}
              @click=${(e) => {
                    e.stopPropagation();
                    this.toggle(g.row.id);
                }}
            >
              <vitesse-icon name="chevron-right" size="13"></vitesse-icon>
            </button>`
                : nothing;
            return html `
          ${one(g.row, g.index, chevron)}
          <!-- The product's one disclosure animation: grid rows 0fr → 1fr over an overflow-hidden
               child, on the shared duration and curve. The agents are always in the DOM, because
               rendering them on open would give the browser nothing to animate from. -->
          ${g.agents.length
                ? html `<div class="cl-fold ${open ? 'open' : ''}">
                <div class="cl-fold-body">${g.agents.map((a) => one(a.row, a.index, nothing))}</div>
              </div>`
                : nothing}
        `;
        })}
    </div>`;
    }
};
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "rows", void 0);
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "onPick", void 0);
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "onReveal", void 0);
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "selected", void 0);
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "onSelect", void 0);
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "toolbar", void 0);
__decorate([
    property({ attribute: false })
], VitesseConversationList.prototype, "actions", void 0);
__decorate([
    property({ type: Number })
], VitesseConversationList.prototype, "total", void 0);
__decorate([
    property({ type: Boolean })
], VitesseConversationList.prototype, "pageScroll", void 0);
__decorate([
    property({ type: Boolean })
], VitesseConversationList.prototype, "filtered", void 0);
__decorate([
    property()
], VitesseConversationList.prototype, "emptyLabel", void 0);
__decorate([
    property({ type: Boolean })
], VitesseConversationList.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean })
], VitesseConversationList.prototype, "precise", void 0);
__decorate([
    state()
], VitesseConversationList.prototype, "shut", void 0);
VitesseConversationList = __decorate([
    customElement('vitesse-conversation-list')
], VitesseConversationList);
export { VitesseConversationList };
/**
 * The sub-line: who is in it, how big it is, when it last moved, what it cost.
 *
 * Assembled in ONE place, and exported, because this is precisely where the two lists had drifted:
 * one described a row as `userId · timestamp · N messages` and the other as `date · N messages`.
 *
 * NO ID. It identifies nothing a person is looking for — every row begins conv- and the rest is
 * noise on a line whose whole job is to be read at a glance.
 */
export { fmtCost, fmtNum };
export function conversationSubtitle(r, precise = false, withCost = true) {
    const parts = [];
    // Only once there is more than one. On a thread you hold alone this is the same value on every
    // row, which is the definition of something not worth the space.
    if (r.users && r.users.length > 1)
        parts.push(t('ai.peopleCount', { n: r.users.length }));
    // `count`, not `n`: i18next pluralises on that name and only that name, so the old call could
    // never select a singular and a one-message thread read "1 messages".
    parts.push(t('ai.messageCount', { count: r.messageCount }));
    parts.push(precise ? fmtDateTime(r.atMs) : fmtWhen(r.atMs));
    // Cost is a console concern — the audit view wants it. In the rail it is noise on a line whose job
    // is to be read at a glance, so the hub asks for it off. Absent stays absent either way.
    if (withCost && typeof r.costUsd === 'number' && r.costUsd > 0)
        parts.push(fmtCost(r.costUsd));
    return parts.join(' · ');
}
/**
 * How long ago, in words, falling back to a date once "days ago" stops being useful.
 *
 * Shared because it is part of how a conversation is described, and the two surfaces describing the
 * same row differently is the whole reason this file exists. The console asks for `precise` instead
 * — an audit view needs the exact instant, and "2 days ago" is not something you can cross-reference
 * against a log.
 */
export function fmtWhen(ms) {
    const mins = Math.round((Date.now() - ms) / 60_000);
    if (mins < 1)
        return t('ai.justNow');
    if (mins < 60)
        return t('ai.minutesAgo', { n: mins });
    const hrs = Math.round(mins / 60);
    if (hrs < 24)
        return t('ai.hoursAgo', { n: hrs });
    const days = Math.round(hrs / 24);
    if (days <= 6)
        return t('ai.daysAgo', { n: days });
    return fmtDate(ms);
}
//# sourceMappingURL=conversation-list.js.map